import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme as useUITheme } from '../ui/theme-provider';
import { useTheme as useReduxTheme } from '../../redux/themeprovider';
import { setTheme } from '../../redux/theme-slice';
/**
 * Component that synchronizes themes between the UI theme system and Redux theme system.
 * This ensures both systems remain in sync when theme changes occur in either one.
 * Supports all themes: light, dark, mellowYellow, purpleRain, blueSky, and system.
 */
function ThemeSynchronizer() {
    const dispatch = useDispatch();
    const { theme: uiTheme, setTheme: setUITheme } = useUITheme();
    const { theme: reduxTheme } = useReduxTheme();
    const currentThemeMode = useSelector((state) => state.theme.current);
    const systemThemeHandlerRef = useRef(null);
    const isInitialSyncRef = useRef(true);
    // Helper function to get the system theme preference
    const getSystemTheme = () => {
        if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
            return 'dark';
        }
        return 'light';
    };
    // Helper function to check if a theme is a valid ThemeMode
    const isValidThemeMode = (theme) => {
        return ['light', 'dark', 'mellowYellow', 'purpleRain', 'blueSky'].includes(theme);
    };
    // Helper function to check if a theme is 'system'
    const isSystemTheme = (theme) => {
        return theme === 'system';
    };
    // Initial synchronization on mount
    useEffect(() => {
        if (isInitialSyncRef.current) {
            // If UI theme is system, we need to apply the system preference
            if (isSystemTheme(uiTheme)) {
                const systemTheme = getSystemTheme();
                // Apply system theme to document
                document.documentElement.classList.remove('light', 'dark', 'mellowYellow', 'purpleRain', 'blueSky');
                document.documentElement.classList.add(systemTheme);
                document.documentElement.setAttribute('data-theme', systemTheme);
                // Update Redux with the actual system theme
                if (currentThemeMode !== 'system') {
                    dispatch(setTheme('system'));
                }
            }
            // If Redux theme is system but UI theme is not, update UI theme
            else if (currentThemeMode === 'system' && !isSystemTheme(uiTheme)) {
                setUITheme('system');
            }
            // If themes are out of sync, synchronize them
            else if (uiTheme !== currentThemeMode && isValidThemeMode(uiTheme)) {
                dispatch(setTheme(uiTheme));
            }
            isInitialSyncRef.current = false;
        }
    }, [uiTheme, currentThemeMode, dispatch, setUITheme]);
    // When UI theme changes, update Redux theme
    useEffect(() => {
        // Skip during initial sync
        if (isInitialSyncRef.current)
            return;
        // For 'system' theme, update Redux to store 'system'
        if (isSystemTheme(uiTheme)) {
            if (currentThemeMode !== 'system') {
                dispatch(setTheme('system'));
            }
            return;
        }
        // For non-system themes, check if the theme is valid and needs updating
        if (isValidThemeMode(uiTheme) && uiTheme !== currentThemeMode) {
            dispatch(setTheme(uiTheme));
        }
    }, [uiTheme, dispatch, currentThemeMode]);
    // When Redux theme changes, update UI theme
    useEffect(() => {
        // Skip during initial sync
        if (isInitialSyncRef.current)
            return;
        // If Redux theme is 'system', ensure UI theme is also 'system'
        if (currentThemeMode === 'system' && !isSystemTheme(uiTheme)) {
            setUITheme('system');
            return;
        }
        // Don't update UI theme if it's set to 'system' - we want to preserve that option
        if (isSystemTheme(uiTheme)) {
            return;
        }
        // Update UI theme to match Redux theme if they're different
        if (currentThemeMode !== uiTheme) {
            setUITheme(currentThemeMode);
        }
    }, [currentThemeMode, setUITheme, uiTheme]);
    // Listen for system theme changes when UI theme is set to 'system'
    useEffect(() => {
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        // Clean up previous listener if it exists
        if (systemThemeHandlerRef.current) {
            mediaQuery.removeEventListener('change', systemThemeHandlerRef.current);
            systemThemeHandlerRef.current = null;
        }
        // Only set up listener if theme is 'system'
        if (isSystemTheme(uiTheme) || currentThemeMode === 'system') {
            const handleChange = (e) => {
                const newSystemTheme = e.matches ? 'dark' : 'light';
                // Update the document classes directly for immediate visual feedback
                document.documentElement.classList.remove('light', 'dark');
                document.documentElement.classList.add(newSystemTheme);
                document.documentElement.setAttribute('data-theme', newSystemTheme);
            };
            // Store the handler for cleanup
            systemThemeHandlerRef.current = handleChange;
            // Add listener for changes
            mediaQuery.addEventListener('change', handleChange);
        }
        // Cleanup
        return () => {
            if (systemThemeHandlerRef.current) {
                mediaQuery.removeEventListener('change', systemThemeHandlerRef.current);
            }
        };
    }, [uiTheme, currentThemeMode]);
    // This component doesn't render anything
    return null;
}
;
export default ThemeSynchronizer;
